import { useToasts } from "@certn/cascadia";
import { Layout, Loader } from "@certn/cascadia-react";
import qs from "qs";
import { useTranslation } from "react-i18next";
import { useIdleTimer } from "react-idle-timer";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";

import { AppFooter } from "../components/AppFooter";
import { AppHeader } from "../components/AppHeader";
import { useUser } from "../hooks/api/useUser";
import { ApplicantDetailsPage } from "../pages/auth/ApplicantDetailsPage";
import { useAuth } from "../pages/auth/hooks";

export function AuthOutlet() {
  const location = useLocation();
  const { t } = useTranslation();
  const { user, isLoading: userLoading, mutate } = useUser();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { show } = useToasts();

  const onIdle = () => {
    if (window.location.hostname !== "localhost") {
      logout();
      navigate("/auth/welcome");
      show({
        title: t("Your session has expired due to inactivity"),
        type: "info",
      });
    } else console.warn("Session idle timeout reached");
  };

  useIdleTimer({
    onIdle,
    timeout: 3600000, // 1 hour
    throttle: 500,
  });

  if (userLoading) {
    return <Loader data-testid="loader" overlay />;
  }

  if (!user) {
    const queryParams = qs.parse(location.search, {
      ignoreQueryPrefix: true,
    });

    if (typeof queryParams.applicantId === "string") {
      queryParams.redirect = `/apply/${queryParams.applicantId}`;
      return <Navigate to={`/auth/welcome?${qs.stringify(queryParams)}`} />;
    }

    return <Navigate to={`/auth/welcome?redirect=${location.pathname}`} />;
  }

  if (!user.personal_profile) {
    return (
      <Layout
        layout="auth"
        header={<AppHeader hideSettings />}
        footer={<AppFooter />}
      >
        <ApplicantDetailsPage onSuccess={mutate} />
      </Layout>
    );
  }

  return <Outlet />;
}
